import { useState, type ReactNode } from "react";
import { Icon } from "@iconify/react";
import { NavLink } from "react-router-dom";
import { Button } from "@/shared/components/button";
import { cn } from "@/shared/lib/utils";
import { Tooltip } from "@/shared/components/misc";

interface SidebarProfileCardProps {
  /** If provided, the whole card becomes a NavLink to this path (highlights when active). */
  to?: string;
  avatarSrc?: string | null;
  avatarInitials: string;
  /** Tailwind bg-* class for the avatar circle. */
  avatarBg?: string;
  name: ReactNode;
  /** e.g. "Male · Age 34 · Jan 1, 1990" — omit for a name-only card. */
  detailsLine?: ReactNode;
  /** e.g. an "Incomplete Profile" nudge — omit when there's nothing to flag. */
  alertSlot?: ReactNode;
  /**
   * Shows the up/down arrow that opens and closes the details line and alert.
   * The card starts closed (avatar + name only). Without it, details always show.
   */
  showChevron?: boolean;
  onClick?: () => void;
}

/**
 * "Male · Age 34 · Jan 1, 1990" as parts that wrap whole. Each part carries its own
 * dot in front; the row is pulled left by one dot's width and clipped, so the dot
 * of whichever part starts a line is hidden: never a dot at the start of a line.
 */
function splitDetails(details: ReactNode): ReactNode {
  if (typeof details !== "string" || !details.includes(" · ")) return details;
  return (
    <span className="-ml-3 flex flex-wrap">
      {details.split(" · ").map((part, i) => (
        <span key={i} className="before:inline-block before:w-3 before:text-center before:content-['·']">
          {part}
        </span>
      ))}
    </span>
  );
}

export function SidebarProfileCard({
  to,
  avatarSrc,
  avatarInitials,
  avatarBg = "bg-[#180047]",
  name,
  detailsLine,
  alertSlot,
  showChevron = true,
  onClick,
}: SidebarProfileCardProps) {
  const collapsible = showChevron && Boolean(detailsLine || alertSlot);
  const [open, setOpen] = useState(false);
  const showDetails = !collapsible || open;
  const details = showDetails ? detailsLine : undefined;

  const body = (
    <>
      {/* A name-only card centres the name on the avatar; with a details line it sits at the top. */}
      <div className={cn("flex min-w-0 gap-2.5", details ? "items-start" : "items-center", collapsible && "pr-7")}>
        <div
          className={cn(
            "flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full text-xs font-bold text-white",
            avatarBg,
          )}
        >
          {avatarSrc ? <img src={avatarSrc} alt="" className="h-full w-full object-cover" /> : avatarInitials}
        </div>
        <div className="min-w-0">
          <p className="truncate text-[15px] font-bold leading-tight text-[#0A2947]">{name}</p>
          {details && <p className="mt-1 overflow-hidden text-sm leading-snug text-[#6B7280]">{splitDetails(details)}</p>}
        </div>
      </div>
      {showDetails && alertSlot && <div className="mt-3">{alertSlot}</div>}
    </>
  );

  return (
    <div className="relative px-3 py-2">
      {onClick ? (
        <Button
          variant="outline"
          onClick={onClick}
          className="!block !h-auto !w-full !whitespace-normal !rounded-2xl !border-[#E5E7EB] !bg-white !px-3.5 !py-3 !text-left hover:!border-[#E4002B]/30"
        >
          {body}
        </Button>
      ) : to ? (
        <NavLink
          to={to}
          className={({ isActive }) =>
            cn(
              "block rounded-2xl border bg-white px-3.5 py-3 transition-colors",
              isActive ? "border-[#E4002B]/50" : "border-[#E5E7EB] hover:border-[#E4002B]/30",
            )
          }
        >
          {body}
        </NavLink>
      ) : (
        <div className="rounded-2xl border border-[#E5E7EB] bg-white px-3.5 py-3">{body}</div>
      )}

      {/* Its own button beside the card (not inside it): the arrow only opens /
          closes the details, a click anywhere else on the card still opens the profile. */}
      {collapsible && (
        // Same hint style as the sidebar's own "Hide sidebar" arrow.
        <Tooltip title={open ? "Hide profile details" : "Show profile details"} placement="right">
          <Button
            variant="ghost"
            size="icon"
            aria-expanded={open}
            aria-label={open ? "Hide profile details" : "Show profile details"}
            onClick={() => setOpen((v) => !v)}
            className="!absolute right-5 top-[26px] !h-7 !w-7 !min-w-0 !rounded-full !p-0 hover:!bg-[#F5F5F5]"
          >
            <Icon icon={open ? "lucide:chevron-up" : "lucide:chevron-down"} className="h-4 w-4 text-[#9CA3AF]" />
          </Button>
        </Tooltip>
      )}
    </div>
  );
}
